import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { Task } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { createMutationCallbacks } from '@klareospace/pharos-lib-web-utils';

import { orderKeys } from '&/features/orders/api';

import type { TaskAssignmentInput } from '../api';
import { createTasksApi, taskKeys } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AssignTaskParams {
  data: TaskAssignmentInput;
  taskId: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Wraps PATCH /tasks/:id/assignment. On success, invalidates the order's task list so `TasksSection` refetches. */
export function useAssignTask(orderId: string): UseMutationResult<Task, Error, AssignTaskParams> {
  const client = useApiClient('deliveries');
  const api = createTasksApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('tasks');

  return useMutation<Task, Error, AssignTaskParams>({
    mutationFn: ({ data, taskId }: AssignTaskParams) => api.assignTask(taskId, data),
    ...createMutationCallbacks({
      errorKey: 'assignment.errors.assignFailed',
      invalidateKeys: ({ taskId }: AssignTaskParams) => [
        orderKeys.tasks(orderId),
        orderKeys.detail(orderId),
        orderKeys.history(orderId),
        orderKeys.lists(),
        taskKeys.history(taskId),
      ],
      queryClient,
      successKey: 'assignment.success.assigned',
      t,
    }),
  });
}
